const trimOrNull = value => {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed || null;
};

const parseTrackingData = trackingData => {
  if (!trackingData) {
    return null;
  }
  if (typeof trackingData === "object") {
    return trackingData;
  }
  try {
    return JSON.parse(trackingData);
  } catch (_) {
    return null;
  }
};

const pickAdCreativeName = data =>
  trimOrNull(data?.ad_name) || trimOrNull(data?.creative_name);

const pickAdNameFallback = data =>
  pickAdCreativeName(data) ||
  trimOrNull(data?.campaign_name) ||
  trimOrNull(data?.utm_campaign) ||
  trimOrNull(data?.utm_content) ||
  trimOrNull(data?.adset_name);

const pickAdId = data => {
  if (!data || typeof data !== "object") {
    return null;
  }

  const candidates = [
    data.ad_id,
    data.adId,
    data["ad.id"],
    data.adID,
  ];

  for (const candidate of candidates) {
    const value = trimOrNull(
      candidate == null ? null : String(candidate)
    );
    if (value) {
      return value;
    }
  }

  return null;
};

/**
 * Nome legível do anúncio (ad_name / creative_name).
 * Não usa campaign/utm — use getPixelAdDisplayName para fallback de exibição.
 */
export const getPixelCreativeName = pixelTracking => {
  const data = parseTrackingData(pixelTracking?.trackingData);
  const fromTracking = pickAdCreativeName(data);
  if (fromTracking) {
    return fromTracking;
  }

  if (pixelTracking?.creativeName?.trim()) {
    const stored = pixelTracking.creativeName.trim();
    if (data?.campaign_name?.trim() === stored) {
      return null;
    }
    return stored;
  }

  return null;
};

export const getLeadCreativeName = lead => pickAdCreativeName(lead);

/**
 * Nome para exibir no ticket: prioriza ad_name, senão campaign/utm.
 */
export const getPixelAdDisplayName = pixelTracking => {
  const data = parseTrackingData(pixelTracking?.trackingData);
  const fromData = pickAdNameFallback(data);
  if (fromData) {
    return fromData;
  }

  return getPixelCreativeName(pixelTracking);
};

export const getLeadAdDisplayName = lead => pickAdNameFallback(lead);

export const getPixelCampaignName = pixelTracking => {
  const data = parseTrackingData(pixelTracking?.trackingData);
  return trimOrNull(data?.campaign_name);
};

export const getLeadCampaignName = lead => trimOrNull(lead?.campaign_name);

/**
 * AD ID numérico/real da Meta. Não usa utm_content (slug ≠ ID).
 */
export const getLeadAdId = leadOrTracking => {
  if (!leadOrTracking) {
    return null;
  }

  const direct = pickAdId(leadOrTracking);
  if (direct) {
    return direct;
  }

  const data = parseTrackingData(leadOrTracking.trackingData);
  return pickAdId(data);
};

export const getPixelAdId = pixelTracking => getLeadAdId(pixelTracking);

/**
 * Linha única para UI (lista/header): "Anúncio: Nome · AD ID: 123"
 * Evita segunda linha / tipografia diferente no ticket.
 */
export const formatPixelAdLine = (pixelTracking, labels = {}) => {
  const creativeName = getPixelAdDisplayName(pixelTracking);
  const adId = getPixelAdId(pixelTracking);
  const parts = [];

  if (creativeName) {
    parts.push(`${labels.adLabel || "Anúncio"}: ${creativeName}`);
  }
  if (adId) {
    parts.push(`${labels.adId || "AD ID"}: ${adId}`);
  }

  return parts.length ? parts.join(" · ") : null;
};

/**
 * WA Tracking local (match por mensagem) gravado em trackingData._wa_tracking.
 */
export const getWaTrackingMeta = pixelTracking => {
  const data = parseTrackingData(pixelTracking?.trackingData);
  const wa = data?._wa_tracking;
  if (!wa || typeof wa !== "object") {
    return null;
  }
  const name = trimOrNull(wa.name);
  const id = trimOrNull(wa.id);
  if (!name && !id) {
    return null;
  }
  return {
    id: id || null,
    name: name || id,
    campaignId: trimOrNull(wa.campaign_id),
    adId: trimOrNull(wa.ad_id),
    adsetId: trimOrNull(wa.adset_id),
  };
};

export const formatWaTrackingLine = (pixelTracking, labels = {}) => {
  const wa = getWaTrackingMeta(pixelTracking);
  if (!wa) return null;
  return `${labels.trackingLabel || "Tracking"}: ${wa.name}`;
};
